// =====================================================================
// src/lib/dashboard.js — o dashboard do dono (proprietário/imobiliária), sem DOM
//
// Cópia da conta de `renderDashboard` (MdM 2386), `dashboardSection`
// (1997), `_flatStatus` (1942), `_receitaDoDia` (1977), `_dashKpisAtuais`
// (2205) e `dashSetPeriodo` (2167). A tela (`src/modulos/dashboard.js`)
// copia a marcação: a fila de KPIs, a barra de período, uma seção por
// proprietário com o banner "Receita do dia", os stat-cards e os cards de
// flat com as três bolinhas.
//
// Quem vê: a IMOBILIÁRIA — uma seção por proprietário dos flats dela, como
// a Monteiro vê no MdM. (O proprietário, que via a dele, saiu na 0101: não
// tem login.) O hotel não tem este dashboard; tem o Painel.
//
// O FINANCEIRO É TRAVADO POR CONTRATO (Hugo, 20 e 21/09): "hotel nunca vê
// valor de reserva ou diária; imobiliária só vê se comprar o módulo
// financeiro; para a Monteiro de Moraes está tudo sempre liberado". Aqui
// isso é o nível do módulo `financeiro` no contexto — `comFinanceiro` —
// e o banco só entrega valor à imobiliária da reserva (0015). Sem o
// módulo, os cards de dinheiro NÃO aparecem (o MdM faz o mesmo com
// `isAdmin()`): a tela fica pronta, o contrato acende.
//
// Divergências de domínio, obrigatórias:
//   · sem `lancamentos`, `despesas_operacionais`, condomínio e taxa de
//     administração: essas tabelas não existem aqui ainda (módulo
//     Financeiro, dez/2026). Receita = valor das reservas; Despesas e
//     Saldo entram com o módulo — não se inventa número;
//   · "Indisponível" é o `unidades.bloqueio` da 0067 (Indisponível ou
//     Manutenção, o "Bloqueio manual" do MdM): a terceira bolinha, laranja,
//     como lá. O seletor, que no MdM mora no modal do flat, aqui fica no
//     detalhe do card — só para quem responde pelo quarto (lib/bloqueio.js);
//   · Recibos Pix é do MdM e ficou de fora; os KPIs do dono são os de
//     hospedagem (hospedados hoje, chegam/saem amanhã), o de Intercorrências
//     para a imobiliária (0099, `modulos/intercorrencias.js`) e, com
//     financeiro, receita do dia e do período;
//   · "Flat N" vira "Quarto N"; cancelada e no-show não contam.
// =====================================================================
import { somarDias, noites, hospedePrincipal, fimDaOcupacao, ocupaANoite } from './reservas.js';

const ativa = r => !!r && r.situacao !== 'cancelada' && r.situacao !== 'noshow' && !!r.check_in && !!r.check_out;

// ── O PERÍODO (`_dashPeriodoDefault` 2154, `dashSetPeriodo` 2167) ─────
const p2 = n => String(n).padStart(2, '0');
export function mesDe(dia) {
  const [a, m] = dia.split('-').map(Number);
  return { ini: `${a}-${p2(m)}-01`, fim: `${a}-${p2(m)}-${p2(new Date(a, m, 0).getDate())}` };
}
export function periodoPadrao(hoje) { return mesDe(hoje); }
// 'hoje' | 'mesAtual' | 'mesAnterior' | 'ano' | {ini, fim}
export function periodoDe(tipo, hoje) {
  if (tipo === 'hoje') return { ini: hoje, fim: hoje };
  if (tipo === 'mesAtual') return mesDe(hoje);
  if (tipo === 'mesAnterior') { const [a, m] = hoje.split('-').map(Number); return mesDe(m === 1 ? `${a - 1}-12-01` : `${a}-${p2(m - 1)}-01`); }
  if (tipo === 'ano') return { ini: hoje.slice(0, 4) + '-01-01', fim: hoje.slice(0, 4) + '-12-31' };
  return { ini: tipo.ini, fim: tipo.fim };
}
export function validarPeriodo({ ini, fim }) {
  if (!ini || !fim) return ['Selecione data inicial e final.'];
  if (ini > fim) return ['A data inicial deve ser antes da final.'];
  return [];
}
export function rotuloDoPeriodo({ ini, fim }, hoje) {
  const br = d => { const [a, m, dd] = d.split('-'); return `${dd}/${m}/${a}`; };
  const p = periodoPadrao(hoje);
  return ini === p.ini && fim === p.fim ? `Mês atual (${br(ini)} → ${br(fim)})` : `${br(ini)} → ${br(fim)}`;
}
export function eMesAtual({ ini, fim }, hoje) { const p = periodoPadrao(hoje); return ini === p.ini && fim === p.fim; }

// ── A RECEITA DO DIA (`_receitaDoDia`, 1977): pro-rata por noites ─────
// `valorDe(r)` devolve o valor total da reserva ou null (sem financeiro,
// ou reserva sem valor definido) — só o que tem valor entra.
export function receitaDoDia(reservas, dia, valorDe) {
  let rec = 0;
  for (const r of reservas || []) {
    if (!ativa(r) || dia < r.check_in || dia >= r.check_out) continue;
    const v = Number(valorDe(r)); if (!v || Number.isNaN(v)) continue;
    rec += v / Math.max(1, noites(r.check_in, r.check_out));
  }
  return rec;
}

// ── O ESTADO DO QUARTO HOJE (`_flatStatus`, 1942) ────────────────────
// Na ordem do MdM: ocupado (a reserva de hoje), indisponível (o bloqueio
// manual, 0067) ou disponível (com a próxima, se houver).
export function estadoDoQuarto(reservas, unidadeId, hoje, bloqueio = null) {
  const minhas = (reservas || []).filter(r => ativa(r) && r.unidade_id === unidadeId);
  const agora = minhas.find(r => ocupaANoite(r, hoje));   // 0096: quem saiu antes já não ocupa
  if (agora) return { estado: 'ocupado', reserva: agora };
  if (bloqueio === 'indisponivel' || bloqueio === 'manutencao') return { estado: 'indisponivel', reserva: null };
  const prox = minhas.filter(r => r.check_in > hoje).sort((a, b) => a.check_in.localeCompare(b.check_in))[0] || null;
  return { estado: 'disponivel', reserva: prox };
}

// ── UMA SEÇÃO (`dashboardSection`, 1997) ─────────────────────────────
export function secao(titulo, quartos, reservas, hoje, periodo, { comFinanceiro = false, valorDe = () => null } = {}) {
  const ids = new Set(quartos.map(q => q.id));
  const rs = (reservas || []).filter(r => ids.has(r.unidade_id));
  const noPeriodo = rs.filter(r => ativa(r) && r.check_in <= periodo.fim && r.check_out >= periodo.ini);
  const s = {
    titulo, quartos,
    ativas: rs.filter(r => r.situacao === 'confirmada').length,
    checkinHoje: rs.filter(r => ativa(r) && r.check_in === hoje).length,
    checkoutHoje: rs.filter(r => ativa(r) && fimDaOcupacao(r) === hoje).length,   // 0096: o dia da saída real
    reservasNoPeriodo: noPeriodo.length,
    estados: quartos.map(q => ({ quarto: q, ...estadoDoQuarto(rs, q.id, hoje, q.bloqueio) })),
    comFinanceiro,
  };
  if (comFinanceiro) {
    s.receitaHoje = receitaDoDia(rs, hoje, valorDe);
    s.receitaPeriodo = noPeriodo.reduce((t, r) => t + (Number(valorDe(r)) || 0), 0);
  }
  return s;
}

// ── AS SEÇÕES (`renderDashboard`, 2396): uma por proprietário ────────
// Os quartos da imobiliária agrupados pelo dono de cada um ("Outros" para
// quarto sem dono, no fim).
export function gruposDoDono(unidades) {
  const m = new Map();
  for (const u of (unidades || []).filter(u => u.ativo !== false)) {
    const p = (u.proprietarios && u.proprietarios.nome) || 'Outros';
    if (!m.has(p)) m.set(p, []);
    m.get(p).push(u);
  }
  return [...m.keys()].sort((a, b) => a === 'Outros' ? 1 : b === 'Outros' ? -1 : a.localeCompare(b))
    .map(p => ({ titulo: p, quartos: m.get(p).slice().sort((a, b) => String(a.numero).localeCompare(String(b.numero), undefined, { numeric: true })) }));
}

// ── OS KPIs DO TOPO (`_dashKpisAtuais`, 2205), os de hospedagem ──────
export function kpis(reservas, hoje, { comFinanceiro = false, valorDe = () => null } = {}) {
  const rs = (reservas || []).filter(ativa);
  const amanha = somarDias(hoje, 1);
  const k = {
    hospedadosHoje: rs.filter(r => ocupaANoite(r, hoje)).length,
    checkinsAmanha: rs.filter(r => r.check_in === amanha).length,
    checkoutsAmanha: rs.filter(r => fimDaOcupacao(r) === amanha).length,
  };
  if (comFinanceiro) {
    k.receitaHoje = receitaDoDia(rs, hoje, valorDe);
    // "Receita no mês … até hoje": pro-rata dia a dia, do 1º até hoje
    const { ini } = mesDe(hoje);
    let t = 0; for (let d = ini; d <= hoje; d = somarDias(d, 1)) t += receitaDoDia(rs, d, valorDe);
    k.receitaMes = t;
  }
  return k;
}

// A borda do card (2076) e as três bolinhas (2102–2104), na ordem do MdM.
export const BORDA = Object.freeze({ ocupado: '#fca5a5', indisponivel: '#fde68a', disponivel: '#bbf7d0' });
export const BOLINHAS = Object.freeze([['#dc2626', 'ocupado', 'Ocupado'], ['#059669', 'disponivel', 'Disponível'], ['#d97706', 'indisponivel', 'Indisponível']]);

// As linhas do detalhe do card de flat (2069): o que abre ao clicar.
export function linhasDoDetalhe(e, { comFinanceiro = false, valorDe = () => null, dataBR = d => d } = {}) {
  const r = e.reserva;
  if (e.estado === 'ocupado' && r) {
    const l = [{ forte: true, texto: hospedePrincipal(r) || '-' }, { texto: 'Check-in: ' + dataBR(r.check_in) }, { texto: 'Check-out: ' + dataBR(r.check_out) }, { texto: 'Plataforma: ', plataforma: r.plataforma || 'Direto' }];
    if (comFinanceiro) l.push({ texto: 'Valor: ', valor: valorDe(r) });
    return l;
  }
  if (e.estado === 'indisponivel') return [{ cor: '#d97706', texto: 'Indisponível' }];
  if (r) return [{ cor: '#059669', texto: 'Disponível agora' }, { texto: 'Próxima reserva:' }, { forte: true, texto: hospedePrincipal(r) || '-' }, { texto: 'Check-in: ' + dataBR(r.check_in) }];
  return [{ cor: '#059669', texto: 'Disponível' }, { texto: 'Sem reservas futuras.' }];
}

// O nome da casa no título: a imobiliária vê "Dashboard — Monteiro de
// Moraes" por seção (o dono de cada grupo). Só a imobiliária é "casa"
// (0101: o proprietário não tem login).
export function quemE(contexto) {
  if (contexto && contexto.imobiliaria && contexto.imobiliaria.id) return { tipo: 'imobiliaria', nome: contexto.imobiliaria.nome };
  return null;
}
